import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from './api'

// Image generation is queued server-side, so the UI submits and then polls.
//
// The task id is the receipt: if the tab is closed or the laptop sleeps, the
// work still finishes and the result is on the task row. Nothing paid-for is
// lost by navigating away, which was the actual reason for the queue -- the
// synchronous version could store two plates and lose the response carrying
// their keys.

export type TaskStatus = 'queued' | 'running' | 'done' | 'failed' | 'cancelled'

export type Task<R = unknown, I = Record<string, unknown>> = {
  id: string
  workspace_id: string
  // Kept in step with repository.Task* on the server. Plates are gone: an
  // avatar is a photograph now, and nothing is generated to make one.
  kind: 'generate_avatar' | 'generate_look' | 'clean_avatar' | 'extract_brand' | 'draft_script' | 'transcribe_asset'
  status: TaskStatus
  /** What was submitted. Read back on resume so a reload can restore the form
   *  fields too -- restoring only the task id leaves the page watching work
   *  whose inputs it can no longer show. */
  input: I
  result: R | null
  error: string | null
  cost_cents: number
  created_at: string
  started_at: string | null
  finished_at: string | null
}

export type PlatesResult = { closeup_key: string; half_key: string }
export type LookResult = { look_id: string; start_frame_key: string }

const TERMINAL = new Set<TaskStatus>(['done', 'failed', 'cancelled'])

/** Submits work and follows it to completion.
 *
 *  Returns the live task, so a caller can distinguish "waiting for a worker"
 *  from "generating" -- worth showing, because the two feel very different
 *  when someone is sitting watching a spinner. */
export function useGeneration<R, I = Record<string, unknown>>(ws: string, path: string) {
  // The task id is persisted, not just held in state.
  //
  // Without this the queue only half works: the worker finishes and the result
  // lands in the database, but a refresh loses the id and the screen has
  // forgotten what it was watching. For plates that means paying twice for
  // work that already succeeded -- the exact failure the queue exists to
  // prevent.
  const storageKey = `ugc.task.${ws}.${path}`
  const [taskID, setTaskIDState] = useState<string | null>(
    () => localStorage.getItem(storageKey),
  )
  const setTaskID = (id: string | null) => {
    setTaskIDState(id)
    if (id) localStorage.setItem(storageKey, id)
    else localStorage.removeItem(storageKey)
  }

  const submit = useMutation({
    mutationFn: (body: unknown) => api.post<Task<R, I>>(`/api/workspaces/${ws}/${path}`, body),
    onSuccess: t => setTaskID(t.id),
  })

  const task = useQuery({
    queryKey: ['task', ws, taskID],
    queryFn: () => api.get<Task<R, I>>(`/api/workspaces/${ws}/tasks/${taskID}`),
    enabled: !!taskID,
    // Poll while it is live; stop the moment it settles.
    refetchInterval: q => {
      const t = q.state.data as Task<R, I> | undefined
      return t && TERMINAL.has(t.status) ? false : 1500
    },
    // A restored id can point at a task that no longer exists -- a wiped
    // database in development, or a workspace the user has left. Retrying a
    // 404 forever would leave the UI stuck on a phantom.
    retry: false,
  })

  // Drop a stored id the server does not recognise, so the next render starts
  // clean rather than polling something that will never resolve.
  useEffect(() => {
    if (taskID && task.isError) setTaskID(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskID, task.isError])

  const current = task.data
  return {
    submit,
    task: current,
    // In flight covers both queued and running: from the caller's point of
    // view nothing has come back yet either way.
    busy: submit.isPending || (!!current && !TERMINAL.has(current.status)),
    waiting: current?.status === 'queued',
    result: current?.status === 'done' ? (current.result as R | null) : null,
    failed: current?.status === 'failed' ? current.error : null,
    costUSD: current ? current.cost_cents / 100 : 0,
    // True when the page was reloaded onto work already in progress, so the
    // UI can say "picked this back up" rather than pretending the user just
    // started it.
    resumed: !!current && !submit.data,
    reset: () => { setTaskID(null); submit.reset() },
    error: submit.error,
  }
}

/** Whether there is work to pick back up for this form.
 *
 *  Needed because restoring the task id is not enough on its own: the form
 *  that shows it is opened by a click, and that state is lost on reload too.
 *  A page that quietly holds a finished result behind a closed form is no
 *  better than having lost it. */
export function hasStoredTask(ws: string, path: string): boolean {
  return !!localStorage.getItem(`ugc.task.${ws}.${path}`)
}

/** Forget the work a form was watching.
 *
 *  Needed by any form that can be abandoned: without it, closing the form and
 *  reopening the page restores the task and the form reopens itself, which
 *  reads as the app refusing to let go. Discards a paid-for result, so callers
 *  should ask first when there is one. */
export function clearStoredTask(ws: string, path: string) {
  localStorage.removeItem(`ugc.task.${ws}.${path}`)
}

export function statusLabel(t: Task | undefined): string {
  switch (t?.status) {
    case 'queued': return 'Waiting for a worker…'
    case 'running': return 'Generating… (up to a minute)'
    case 'failed': return 'Failed'
    case 'cancelled': return 'Cancelled'
    default: return ''
  }
}
